import axios from 'axios';
import { toast } from 'react-toastify';

/** Public API origin (build-time via .env.production), same fallback everywhere. */
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.budyguard.com/';

const axiosInstance = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000, // Set timeout to 30 seconds
});

/**
 * Check if the token is a JWT and if it has expired (using exp claim).
 * Returns true if token is expired or invalid, false otherwise.
 */
function isTokenExpired(tokenStr) {
    if (!tokenStr || typeof tokenStr !== 'string') return true;
    try {
        const parts = tokenStr.split('.');
        if (parts.length !== 3) return false; // Not a JWT, let the server validate
        const payload = JSON.parse(atob(parts[1]));
        if (payload.exp && typeof payload.exp === 'number') {
            // exp is in seconds, Date.now() is in ms; allow 10s buffer
            return payload.exp * 1000 < Date.now() - 10000;
        }
        return false;
    } catch {
        return false;
    }
}

function clearAuthAndRedirect(message = 'Session expired. Please sign in again.') {
    localStorage.removeItem('token');
    localStorage.removeItem('userDetail');
    toast.error(message);
    if (window.location.pathname !== '/login') {
        window.location.href = '/login';
    }
}

// Request interceptor to add Authorization header and check token expiration
axiosInstance.interceptors.request.use(
    function (config) {
        const tokenStr = localStorage.getItem('token');
        if (tokenStr) {
            let token = tokenStr;
            try {
                const parsed = JSON.parse(tokenStr);
                if (parsed && typeof parsed === 'string') token = parsed;
                else if (parsed && typeof parsed === 'object' && parsed.token) token = parsed.token;
                else if (parsed) token = String(parsed);
            } catch {
                // use tokenStr as-is
            }
            if (token && isTokenExpired(token)) {
                clearAuthAndRedirect();
                return Promise.reject(new Error('Token expired'));
            }
            if (token) {
                config.headers['Authorization'] = `Bearer ${token}`;
            }
        }
        return config;
    },
    function (error) {
        return Promise.reject(error);
    }
);

const localVersion = localStorage.getItem('frontend_version');
axiosInstance.interceptors.response.use(
    function (response) {
        const frontendVersion = response?.headers?.['frontend_version'] || response?.headers?.['Frontend-Version'];
        if (frontendVersion) {
            localStorage.setItem('frontend_version', frontendVersion);
        }
        
        if(localVersion === frontendVersion){
            return response;
        } else if(localVersion == null || localVersion == undefined || localVersion == 'undefined' || localVersion == 'null'){
            return response;
        } else if(frontendVersion){
            localStorage.clear();
            window.location.href = '/login';
        }
        
        return response;
    },
    function (error) {
        // Handle 401 Unauthorized (e.g. expired token from server)
        if (error?.response?.status === 401) {
            clearAuthAndRedirect('Session expired. Please sign in again.');
        }
        return Promise.reject(error);
    }
);
/** Returns true if token exists and is not expired (for use in PrivateRoute etc.) */
export function isAuthTokenValid() {
    const tokenStr = localStorage.getItem('token');
    if (!tokenStr) return false;
    let token = tokenStr;
    try {
        const parsed = JSON.parse(tokenStr);
        if (parsed && typeof parsed === 'string') token = parsed;
        else if (parsed && typeof parsed === 'object' && parsed.token) token = parsed.token;
        else if (parsed) token = String(parsed);
    } catch {
        // use as-is
    }
    return !!token && !isTokenExpired(token);
}

export default axiosInstance;
